import { DatePipe, NgClass, NgStyle } from '@angular/common';
import { Component, inject, Input, output, signal } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Settings, SettingsCmd } from '@app/core/config/settings';
import { BackendService } from '@app/shared/services/backend.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';

@Component({
  // tslint:disable-next-line:component-selector
  selector: '[app-admin-settings-entry]',
  templateUrl: './admin-settings-entry.component.html',
  imports: [
    DatePipe,
    NgClass,
    NgStyle,
    ReactiveFormsModule,
    TranslatePipe,
  ],
})
export class AdminSettingsEntryComponent {
  private readonly backendService = inject(BackendService);
  private readonly translateService = inject(TranslateService);
  private readonly formBuilder = inject(UntypedFormBuilder);

  readonly deleted = output();
  readonly reloadEntries = output();

  protected readonly settingsForm = signal(this.generateForm());
  protected readonly delChosen = signal(false);

  protected setting: Settings|null = null;

  private generateForm(): UntypedFormGroup {
    return this.formBuilder.group({
      key   : [this.setting?.key   ?? '', Validators.required],
      value : [this.setting?.value ?? '', Validators.required]
    });
  }

  @Input()
  set set(setting: Settings) {
    if (!setting) {
      throw new Error('Setting must exist');
    }
    this.setting = setting;
    this.reset();
  }

  protected submit() {
    if (this.setting) {
      const formValues = this.settingsForm().value;
      const cmd = new SettingsCmd()
      cmd.id = this.setting.id;
      cmd.key = formValues.key;
      cmd.value = formValues.value;
      this.backendService.editSetting(cmd)
        .subscribe({
          next: result => {
            this.setting = result;
            this.reloadEntries.emit();
          },
          error: console.error
        });
    }
  }

  protected reset() {
    this.settingsForm.set(this.generateForm());
  }

  protected confirmDelete() {
    const setting = this.setting;
    if (setting) {
      this.delChosen.set(true);
      Swal.fire({
        text              : this.translateService.instant('admin.settings.delete.warning'),
        icon              : 'warning',
        buttonsStyling    : true,
        showCancelButton  : true,
        confirmButtonText : this.translateService.instant('admin.settings.delete.yes'),
        cancelButtonText  : this.translateService.instant('global.cancel')
      }).then((result) => {
        if (result.value) {
          this.delete(setting.id);
        } else {
          this.delChosen.set(false);
        }
      });
    }
  }

  private delete(settingId: number) {
    this.backendService.deleteSetting(settingId)
      .subscribe({
        next: () => {
          this.showAlert(true);
          this.deleted.emit();
        },
        error: error => {
          this.showAlert(false);
          console.error(error.message);
        }
      });
  }

  private showAlert(success: boolean) {
    const msgCode = success
      ? 'admin.settings.delete.success'
      : 'admin.settings.delete.error';

    Swal.fire({
      text              : this.translateService.instant(msgCode),
      icon              : success ? 'success' : 'error',
      buttonsStyling    : true,
      confirmButtonText : this.translateService.instant('global.ok')
    });
  }
}
